import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Search,
    LifeBuoy,
    BookOpen,
    MessageSquare,
    Activity,
    Users2,
    ArrowUpRight,
    Send,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
    HoneypotField,
    useFormSecurity,
    withFormSecurity,
} from "../lib/formSecurity";

import { SAAS_API_URL, STATUS_SITE_URL } from "../lib/env";

const SAAS_API = SAAS_API_URL;

const QUICK_LINKS = [
    {
        icon: BookOpen,
        title: "Documentation",
        body: "Every module, every workflow, with usage steps and video walkthroughs.",
        href: "/docs",
        cta: "Open docs",
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/25",
    },
    {
        icon: Activity,
        title: "Service status",
        body: "Real-time platform health across all regions, with incident history.",
        href: STATUS_SITE_URL,
        external: true,
        cta: "View status",
        accent: "text-emerald-300",
        accentBg: "bg-emerald-300/10 border-emerald-300/25",
    },
    {
        icon: Users2,
        title: "Community",
        body: "12,000+ operators on Discord — share playbooks, ask anything, ship faster.",
        href: "https://discord.gg/UAdRYSQEkg",
        external: true,
        cta: "Join Discord",
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
    },
    {
        icon: LifeBuoy,
        title: "Email support",
        body: "Business-day response for free tiers, 1-hour SLA on Business and Enterprise.",
        href: "mailto:support@apexbrain.in",
        cta: "Email support",
        accent: "text-amber-glow",
        accentBg: "bg-amber-300/10 border-amber-300/25",
    },
];

const TOP_FAQS = [
    {
        q: "Where do I learn each module from scratch?",
        a: "The /docs page — every pillar has a 2-minute explainer video and a numbered usage guide.",
    },
    {
        q: "What's your support SLA?",
        a: "Free & Startups: best-effort within 1 business day. Growth: 4-hour first response. Business & Enterprise: 1-hour first response, 24/7.",
    },
    {
        q: "Can I bring a friend / colleague in?",
        a: "Yes — your community account is free forever and the Discord is open. Invite the team and ship.",
    },
    {
        q: "How do I report a security issue?",
        a: "Email security@apexbrain.in. We respond within 24 hours and our public bug bounty is on hackerone.com/apexbrain.",
    },
    {
        q: "Is there a community-led knowledge base?",
        a: "Yes — community.apexbrain.in is moderated by ApexBrain operators and the support team.",
    },
    {
        q: "How do I request a feature?",
        a: "Open a thread in the #feedback channel on Discord or vote on roadmap.apexbrain.in. Customer-driven items ship monthly.",
    },
];

export default function HelpPage() {
    const [q, setQ] = useState("");
    const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const { security, setSecurity, resetSecurity } = useFormSecurity();

    const filtered = useMemo(() => {
        if (!q.trim()) return TOP_FAQS;
        const s = q.toLowerCase();
        return TOP_FAQS.filter(
            (f) =>
                f.q.toLowerCase().includes(s) ||
                f.a.toLowerCase().includes(s),
        );
    }, [q]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email and a message.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            toast.error("That email doesn’t look right.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${SAAS_API}/api/public/v1/contact-inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(withFormSecurity({
                    source: "help",
                    name: form.name,
                    email: form.email,
                    topic: form.topic,
                    message: form.message,
                }, security)),
            });
            const json = await res.json();
            if (!res.ok || !json.success) {
                throw new Error(json.message || "Submission failed");
            }
            toast.success(
                "Got it — support will reply within one business day.",
            );
            setForm({ name: "", email: "", topic: "general", message: "" });
            resetSecurity();
        } catch (err) {
            toast.error(err.message || "Could not send your message. Try support@apexbrain.in");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pt-28 lg:pt-32" data-testid="help-page">
            <Toaster position="top-right" theme="dark" richColors />
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // help · support · community
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="help-title"
                        >
                            We answer fast.
                            <br />
                            <span className="gradient-text">
                                And we build with you.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        Search the help center, drop into the community, or
                        write to a human. We staff support globally so help is
                        never a timezone away.
                    </p>
                </div>

                {/* Universal search */}
                <div className="relative max-w-2xl mx-auto px-6 sm:px-0 mt-10">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search help…  e.g. ‘invite teammate’"
                        data-testid="help-search"
                        className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white placeholder:text-zinc-600 font-code transition"
                    />
                </div>
            </section>

            {/* Quick links */}
            <section className="relative py-10">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {QUICK_LINKS.map((l, i) => (
                        <motion.a
                            key={l.title}
                            href={l.href}
                            target={l.external ? "_blank" : undefined}
                            rel={l.external ? "noopener noreferrer" : undefined}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            whileHover={{ y: -3 }}
                            className="group rounded-2xl border border-white/[0.06] bg-obsidian-200/70 backdrop-blur-sm p-6 hover:border-white/15 transition-colors"
                            data-testid={`help-link-${i}`}
                        >
                            <span
                                className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border ${l.accentBg}`}
                            >
                                <l.icon className={`w-4 h-4 ${l.accent}`} />
                            </span>
                            <div className="mt-5 font-outfit text-base font-semibold text-white">
                                {l.title}
                            </div>
                            <p className="mt-1.5 text-xs text-zinc-500 leading-relaxed">
                                {l.body}
                            </p>
                            <div
                                className={`mt-5 inline-flex items-center gap-1.5 text-xs font-code font-semibold ${l.accent}`}
                            >
                                {l.cta}
                                <ArrowUpRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </div>
                        </motion.a>
                    ))}
                </div>
            </section>

            {/* FAQ + Contact */}
            <section className="relative py-16 pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    <div className="lg:col-span-7">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-3">
                            // top questions
                        </p>
                        <h2 className="font-outfit text-2xl sm:text-3xl font-semibold text-white leading-snug mb-7">
                            The ones we get most.
                        </h2>
                        <div className="space-y-3">
                            {filtered.map((f, i) => (
                                <details
                                    key={f.q}
                                    className="group rounded-xl border border-white/[0.08] bg-obsidian-200/70 px-5 py-4 open:bg-white/[0.03] transition"
                                    data-testid={`help-faq-${i}`}
                                >
                                    <summary className="cursor-pointer list-none flex items-center justify-between text-sm font-semibold text-white">
                                        {f.q}
                                        <span className="font-code text-xs text-zinc-500 group-open:rotate-180 transition-transform">
                                            ▾
                                        </span>
                                    </summary>
                                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed font-light">
                                        {f.a}
                                    </p>
                                </details>
                            ))}
                            {filtered.length === 0 && (
                                <p className="text-sm text-zinc-500">
                                    No matches for “{q}”. Try the contact form
                                    on the right.
                                </p>
                            )}
                        </div>
                    </div>

                    <form
                        onSubmit={submit}
                        className="lg:col-span-5 rounded-2xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-6 sm:p-7 space-y-4 relative overflow-hidden h-fit"
                        data-testid="help-contact-form"
                    >
                        <HoneypotField
                            value={security._hp}
                            onChange={(v) => setSecurity((s) => ({ ...s, _hp: v }))}
                        />
                        <div className="absolute -top-20 -right-20 w-56 h-56 bg-electric/20 blur-3xl rounded-full pointer-events-none" />
                        <p className="relative font-code text-[10px] uppercase tracking-[0.2em] text-electric">
                            // contact a human
                        </p>
                        <h3 className="relative font-outfit text-xl font-semibold text-white">
                            Drop us a line.
                        </h3>
                        <p className="relative text-xs text-zinc-400 leading-relaxed">
                            We read every message. Average first response: 4
                            hours business, 18 hours weekends.
                        </p>
                        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input
                                data-testid="help-form-name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                placeholder="Your name"
                                className="rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
                            />
                            <input
                                data-testid="help-form-email"
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                placeholder="Email"
                                className="rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
                            />
                        </div>
                        <select
                            data-testid="help-form-topic"
                            value={form.topic}
                            onChange={(e) =>
                                setForm({ ...form, topic: e.target.value })
                            }
                            className="relative w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 font-code"
                        >
                            <option value="general" className="bg-obsidian-300">
                                General question
                            </option>
                            <option value="billing" className="bg-obsidian-300">
                                Billing
                            </option>
                            <option value="bug" className="bg-obsidian-300">
                                Bug report
                            </option>
                            <option value="security" className="bg-obsidian-300">
                                Security
                            </option>
                            <option value="enterprise" className="bg-obsidian-300">
                                Enterprise / Sales
                            </option>
                        </select>
                        <textarea
                            data-testid="help-form-message"
                            value={form.message}
                            onChange={(e) =>
                                setForm({ ...form, message: e.target.value })
                            }
                            rows={4}
                            placeholder="What can we help with?"
                            className="relative w-full rounded-lg bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-3 py-2.5 placeholder:text-zinc-600"
                        />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="relative group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-5 py-2.5 rounded-full hover:brightness-110 transition disabled:opacity-60"
                            data-testid="help-form-submit"
                        >
                            Send message
                            <Send className="w-3.5 h-3.5" />
                        </button>
                    </form>
                </div>
            </section>

            {/* Community strip */}
            {/* <section className="relative pb-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div
                        className="rounded-3xl border border-white/10 bg-gradient-to-br from-obsidian-100 via-obsidian-200 to-obsidian-300 p-10 sm:p-14 text-center relative overflow-hidden"
                        data-testid="help-community-cta"
                    >
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[70%] h-40 bg-violet-500/25 blur-[120px] rounded-full pointer-events-none" />
                        <MessageSquare className="mx-auto w-6 h-6 text-violet-300" />
                        <p className="mt-3 font-code text-xs uppercase tracking-[0.3em] text-violet-200/80">
                            // community · 12,400 operators
                        </p>
                        <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mt-4 leading-snug">
                            The fastest answers come from operators
                            <br />
                            who’ve already shipped what you’re building.
                        </h2>
                        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                            <a
                                href="https://discord.gg/UAdRYSQEkg"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-violet-500 to-violet-700 px-6 py-3 rounded-full hover:brightness-110 transition btn-glow"
                                data-testid="help-discord-cta"
                            >
                                Join the Discord
                                <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <a
                                href="https://community.apexbrain.in"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-6 py-3 rounded-full hover:bg-white/10 transition"
                                data-testid="help-forum-cta"
                            >
                                Visit the forum
                                <ArrowUpRight className="w-4 h-4 text-violet-300" />
                            </a>
                        </div>
                    </div>
                </div>
            </section> */}
        </div>
    );
}
